import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  Textarea,
  toast,
} from '@mlm/ui';
import { ACCOUNTING_PAYMENT_METHODS, AR_PERMS } from '@mlm/shared';
import {
  createArCreditRefund,
  createArReceipt,
  getArReceipt,
  listArInvoices,
  listArReceipts,
  listFundsAccounts,
  applyArCredit,
  unapplyArApplication,
  voidArCreditRefund,
  voidArReceipt,
  type ArReceiptRow,
} from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { PageHeader } from './_shared';

/**
 * Payments — the collection side of AR (R2 Phase D).
 *
 * The till takes payments; this page is for the work that should not happen at
 * a register: seeing what a payment actually settled, moving it onto the right
 * billing after the fact, and handing unapplied credit back as money.
 */

function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function apiError(e: unknown): string {
  if (isAxiosError(e)) {
    const code = (e.response?.data as { error?: string } | undefined)?.error;
    if (code === 'refund_outstanding') {
      return 'This payment has a refund against it. Void the refund first.';
    }
    if (code === 'insufficient_credit') return 'That is more than the credit available.';
    if (code === 'allocation_exceeds_outstanding') {
      return 'A line is larger than that billing’s remaining balance.';
    }
    if (code === 'allocation_sum_mismatch') return 'The lines add up to more than the payment.';
    return code ?? 'Request failed';
  }
  return 'Request failed';
}

const SELECT_CLASS = 'h-9 w-full rounded-md border bg-background px-2 text-sm';

function MethodSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      id={id}
      className={SELECT_CLASS}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {ACCOUNTING_PAYMENT_METHODS.map((m) => (
        <option key={m} value={m}>
          {m.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

function AccountSelect({
  id,
  value,
  onChange,
  accounts,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  accounts: Array<{ id: string; name: string }>;
}) {
  return (
    <select
      id={id}
      className={SELECT_CLASS}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">— select —</option>
      {accounts.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </select>
  );
}

export function ArReceiptsPage() {
  const canWrite = useHasPerm(AR_PERMS.receiptWrite);
  const canRefund = useHasPerm(AR_PERMS.creditRefundWrite);
  const canVoid = useHasPerm(AR_PERMS.void);
  const [open, setOpen] = useState<string | null>(null);

  const listQ = useQuery({ queryKey: ['ar-receipts'], queryFn: () => listArReceipts({}) });
  const rows = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payments"
        subtitle="Money received from customers. A payment that does not settle a billing in full leaves the rest as that customer's credit, which can be applied later or refunded."
      />

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              No payments recorded yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Doc no</th>
                    <th className="px-4 py-2">Received</th>
                    <th className="px-4 py-2">Method</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-right">Unapplied</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                      onClick={() => setOpen(r.id)}
                    >
                      <td className="px-4 py-2 font-mono text-xs">{r.docNo}</td>
                      <td className="px-4 py-2">{shortDate(r.receivedAt)}</td>
                      <td className="px-4 py-2 capitalize">{r.method.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2">
                        <ReceiptStatus row={r} />
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {peso(r.amount)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {r.status === 'void' ? '—' : peso(r.amountUnapplied)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {open ? (
        <ReceiptDialog
          receiptId={open}
          canWrite={canWrite}
          canRefund={canRefund}
          canVoid={canVoid}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

function ReceiptStatus({ row }: { row: ArReceiptRow }) {
  if (row.status === 'void') return <Badge variant="muted">Void</Badge>;
  if (Number(row.amountUnapplied) > 0) {
    return <Badge variant="warning">{row.kind === 'prepayment' ? 'Credit' : 'Part applied'}</Badge>;
  }
  return <Badge variant="success">Applied</Badge>;
}

function ReceiptDialog({
  receiptId,
  canWrite,
  canRefund,
  canVoid,
  onClose,
}: {
  receiptId: string;
  canWrite: boolean;
  canRefund: boolean;
  canVoid: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [applying, setApplying] = useState(false);

  const detailQ = useQuery({
    queryKey: ['ar-receipt', receiptId],
    queryFn: () => getArReceipt(receiptId),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['ar-receipt', receiptId] });
    void qc.invalidateQueries({ queryKey: ['ar-receipts'] });
    void qc.invalidateQueries({ queryKey: ['ar-invoices'] });
  };

  const unapply = useMutation({
    mutationFn: (id: string) => unapplyArApplication(id, reason.trim() || 'Unapplied by admin'),
    onSuccess: () => {
      toast.success('Unapplied. The billing is open again and the money is back as credit.');
      refresh();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const doVoid = useMutation({
    mutationFn: () => voidArReceipt(receiptId, reason.trim()),
    onSuccess: () => {
      toast.success('Payment voided.');
      refresh();
      onClose();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const voidRefund = useMutation({
    mutationFn: (id: string) => voidArCreditRefund(id, reason.trim() || 'Voided by admin'),
    onSuccess: () => {
      toast.success('Refund voided. The credit is restored.');
      refresh();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const d = detailQ.data;
  const unapplied = Number(d?.receipt.amountUnapplied ?? 0);
  const isVoid = d?.receipt.status === 'void';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{d ? `Payment ${d.receipt.docNo}` : 'Payment'}</DialogTitle>
        </DialogHeader>

        {!d ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="font-mono tabular-nums">{peso(d.receipt.amount)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Unapplied credit</p>
                <p className="font-mono tabular-nums">{peso(d.receipt.amountUnapplied)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Received</p>
                <p>{shortDate(d.receipt.receivedAt)}</p>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                What it settled
              </p>
              {d.applications.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nothing yet — the whole amount is held as this customer's credit.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {d.applications.map((a) => (
                      <tr key={a.id} className="border-b last:border-0">
                        <td className="py-2 font-mono text-xs">{a.docNo}</td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {a.status === 'void' ? 'Unapplied' : shortDate(a.appliedAt)}
                        </td>
                        <td className="py-2 text-right font-mono tabular-nums">{peso(a.amount)}</td>
                        <td className="py-2 text-right">
                          {canWrite && a.status === 'posted' && !isVoid ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={unapply.isPending}
                              onClick={() => unapply.mutate(a.id)}
                            >
                              Unapply
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {d.refunds.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Refunds
                </p>
                <table className="w-full text-sm">
                  <tbody>
                    {d.refunds.map((r) => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="py-2 font-mono text-xs">{r.docNo}</td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {r.status === 'void' ? 'Void' : shortDate(r.refundedAt)}
                        </td>
                        <td className="py-2 text-right font-mono tabular-nums">{peso(r.amount)}</td>
                        <td className="py-2 text-right">
                          {canVoid && r.status === 'posted' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={voidRefund.isPending}
                              onClick={() => voidRefund.mutate(r.id)}
                            >
                              Void
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isVoid && (canWrite || canVoid) && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground" htmlFor="ar-receipt-reason">
                  Reason
                </label>
                <Textarea
                  id="ar-receipt-reason"
                  rows={2}
                  placeholder="Why is this being changed? Recorded on the audit trail."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {d && !isVoid && canWrite && unapplied > 0 ? (
            <Button variant="outline" onClick={() => setApplying(true)}>
              Apply credit
            </Button>
          ) : null}
          {d && !isVoid && canRefund && unapplied > 0 ? (
            <Button variant="outline" onClick={() => setRefunding(true)}>
              Refund credit
            </Button>
          ) : null}
          {d && !isVoid && canVoid ? (
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || doVoid.isPending}
              onClick={() => doVoid.mutate()}
            >
              Void payment
            </Button>
          ) : null}
        </DialogFooter>

        {applying && d ? (
          <ApplyCreditDialog
            receiptId={receiptId}
            customerId={d.receipt.customerId}
            available={unapplied}
            onClose={() => setApplying(false)}
            onDone={() => {
              setApplying(false);
              refresh();
            }}
          />
        ) : null}
        {refunding && d ? (
          <RefundDialog
            receiptId={receiptId}
            maxAmount={unapplied}
            onClose={() => setRefunding(false)}
            onDone={() => {
              setRefunding(false);
              refresh();
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Put a receipt's unapplied credit against an open billing. Together with
 * Unapply this is how a payment gets moved onto the right bill after the fact:
 * take it off the wrong one, put it on the right one.
 *
 * The list is general billings only — consignment ones are settled at the till,
 * where the consignee's holdings and statement live.
 */
function ApplyCreditDialog({
  receiptId,
  customerId,
  available,
  onClose,
  onDone,
}: {
  receiptId: string;
  customerId: string;
  available: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');

  const openQ = useQuery({
    queryKey: ['ar-invoices', 'open-for-credit', customerId],
    queryFn: () => listArInvoices({ customerId, status: 'open' }),
  });
  const partialQ = useQuery({
    queryKey: ['ar-invoices', 'partial-for-credit', customerId],
    queryFn: () => listArInvoices({ customerId, status: 'partial' }),
  });
  const billings = [...(openQ.data ?? []), ...(partialQ.data ?? [])];
  const chosen = billings.find((b) => b.id === invoiceId);
  const outstanding = chosen ? Number(chosen.total) - Number(chosen.amountPaid) : 0;

  const submit = useMutation({
    mutationFn: () =>
      applyArCredit({
        receiptId,
        allocations: [{ invoiceId, amount: Number(amount).toFixed(2) }],
      }),
    onSuccess: (r) => {
      toast.success(`Applied. ${peso(r.creditLeft)} credit left.`);
      onDone();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const valid =
    Boolean(invoiceId) &&
    Number(amount) > 0 &&
    Number(amount) <= available + 0.005 &&
    Number(amount) <= outstanding + 0.005;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Apply credit to a billing</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="apply-billing">
              Billing
            </label>
            <select
              id="apply-billing"
              className={SELECT_CLASS}
              value={invoiceId}
              onChange={(e) => {
                setInvoiceId(e.target.value);
                const b = billings.find((x) => x.id === e.target.value);
                if (b) {
                  const owed = Number(b.total) - Number(b.amountPaid);
                  setAmount(Math.min(owed, available).toFixed(2));
                }
              }}
            >
              <option value="">— select —</option>
              {billings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.docNo} — {peso(Number(b.total) - Number(b.amountPaid))} owing
                </option>
              ))}
            </select>
            {billings.length === 0 && !openQ.isLoading ? (
              <p className="text-xs text-muted-foreground">
                This customer has no open billings to apply credit to.
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="apply-amount">
              Amount
            </label>
            <MoneyInput
              id="apply-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{peso(available)} of credit available.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Handing credit back as money. The account is chosen here and written straight
 * to the register — a refund is a real disbursement, not a bookkeeping entry,
 * so somebody has to say which account it left.
 */
function RefundDialog({
  receiptId,
  maxAmount,
  onClose,
  onDone,
}: {
  receiptId: string;
  maxAmount: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(maxAmount.toFixed(2));
  const [method, setMethod] = useState<string>('bank_transfer');
  const [fundsAccountId, setFundsAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [clientRequestId] = useState(() => crypto.randomUUID());

  const accountsQ = useQuery({
    queryKey: ['funds-accounts', 'active'],
    queryFn: () => listFundsAccounts(true),
  });
  const accounts = accountsQ.data?.rows ?? [];

  const submit = useMutation({
    mutationFn: () =>
      createArCreditRefund({
        receiptId,
        amount: Number(amount).toFixed(2),
        method,
        fundsAccountId,
        reference: reference.trim() || null,
        clientRequestId,
      }),
    onSuccess: () => {
      toast.success('Refund recorded.');
      onDone();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const valid =
    Number(amount) > 0 && Number(amount) <= maxAmount + 0.005 && Boolean(fundsAccountId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refund customer credit</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="crf-amount">
              Amount
            </label>
            <MoneyInput
              id="crf-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {peso(maxAmount)} of unapplied credit is available.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="crf-method">
              Method
            </label>
            <MethodSelect id="crf-method" value={method} onChange={setMethod} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="crf-account">
              Paid from
            </label>
            <AccountSelect
              id="crf-account"
              value={fundsAccountId}
              onChange={setFundsAccountId}
              accounts={accounts}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="crf-ref">
              Reference
            </label>
            <Input
              id="crf-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Cheque or transfer number"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
            Record refund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Record a payment against one billing, from the Billings page. The common
 * case by far — the operator is looking at the bill they were just paid for.
 */
export function RecordPaymentDialog({
  invoiceId,
  customerId,
  outstanding,
  onClose,
  onDone,
}: {
  invoiceId: string;
  customerId: string;
  outstanding: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(outstanding.toFixed(2));
  const [method, setMethod] = useState<string>('bank_transfer');
  const [fundsAccountId, setFundsAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [clientRequestId] = useState(() => crypto.randomUUID());

  const accountsQ = useQuery({
    queryKey: ['funds-accounts', 'active'],
    queryFn: () => listFundsAccounts(true),
  });
  const accounts = accountsQ.data?.rows ?? [];

  const applied = Math.min(Number(amount) || 0, outstanding);
  const credit = Number(((Number(amount) || 0) - applied).toFixed(2));

  const submit = useMutation({
    mutationFn: () =>
      createArReceipt({
        customerId,
        amount: Number(amount).toFixed(2),
        method,
        fundsAccountId: fundsAccountId || null,
        reference: reference.trim() || null,
        clientRequestId,
        // Only ever against THIS billing; anything above its balance stays as
        // credit rather than silently paying down some other bill.
        allocations: applied > 0 ? [{ invoiceId, amount: applied.toFixed(2) }] : [],
      }),
    onSuccess: () => {
      toast.success(
        credit > 0 ? `Payment recorded. ${peso(credit)} kept as credit.` : 'Payment recorded.',
      );
      onDone();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="pay-amount">
              Amount received
            </label>
            <MoneyInput
              id="pay-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {peso(outstanding)} outstanding on this billing.
              {credit > 0 ? ` ${peso(credit)} will be kept as credit.` : ''}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="pay-method">
              Method
            </label>
            <MethodSelect id="pay-method" value={method} onChange={setMethod} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="pay-account">
              Received into
            </label>
            <AccountSelect
              id="pay-account"
              value={fundsAccountId}
              onChange={setFundsAccountId}
              accounts={accounts}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="pay-ref">
              Reference
            </label>
            <Input
              id="pay-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Cheque or transfer number"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={Number(amount) <= 0 || submit.isPending}
            onClick={() => submit.mutate()}
          >
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
